import {Component, ElementRef, HostListener, computed, effect, input, output, viewChild} from '@angular/core';
import {AccountIdentity, accountName, providerLabel} from '../_models/account-identity';

/**
 * Confirms an administrative **second-factor reset** — in a modal, not inline in the table row it was clicked
 * from.
 *
 * ### Why a modal, not the row
 *
 * The same reason `RevokeConfirmDialogComponent` gives, and it is a reproduced defect rather than a
 * preference: swapping a one-line action for a sentence plus two buttons **inside the same `<td>`** grows the
 * row, recomputes the column widths, and shifts every row below it under the cursor — which is how somebody
 * confirms the wrong row. This action destroys a working credential and cannot be undone, so the surface
 * asking for confirmation must not itself be able to cause a mis-click.
 *
 * ### Why the copy says what survives, not only what is destroyed
 *
 * "Reset the second factor" is easy to read as "reset the account", and an operator who believes that will
 * hesitate over the one action that is actually safe to take. The dialog therefore names both halves: the
 * authenticator and the recovery codes go, and the linked accounts, the password login and the granted roles
 * stay. That is not reassurance — it is the server's actual behaviour
 * (`IAccountSecondFactorService.AdminResetSecondFactor`), and it is the difference between an operator helping
 * somebody in five minutes and escalating.
 *
 * ### Dismissible by backdrop and Escape
 *
 * Cancelling costs nothing — the account is untouched either way — so a stray click outside or an `Escape`
 * press is an ordinary **cancel**, the safe direction. Focus starts on *Abbrechen* for the same reason: a
 * stray `Enter` on a destructive prompt should land on the harmless answer.
 */
@Component({
  selector: 'owner-two-factor-reset-dialog',
  standalone: true,
  template: `
    <!-- Backdrop click cancels — the safe direction for a prompt whose cancel costs nothing. -->
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg
                  sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="reset-2fa-heading"
           aria-describedby="reset-2fa-body" (click)="$event.stopPropagation()">
        <div class="space-y-4 p-4 sm:p-6">

          <h2 id="reset-2fa-heading" class="text-xl font-bold tracking-tight text-gray-900">
            Zwei-Faktor-Anmeldung zurücksetzen?
          </h2>

          <div id="reset-2fa-body" class="space-y-3 text-sm text-gray-700">
            <p>
              Konto: <span class="font-semibold text-gray-900">{{ name() }}</span>
            </p>
            @if (address(); as mail) {
              <p>E-Mail: <span class="font-semibold text-gray-900">{{ mail }}</span></p>
            }
            <p>Anbieter: <span class="font-semibold text-gray-900">{{ provider() }}</span></p>

            <p>
              Der Authenticator und alle noch ungenutzten Wiederherstellungscodes dieses Kontos werden
              gelöscht. Die Person richtet bei der nächsten Anmeldung eine neue Authenticator-App ein.
            </p>
            <!-- Named because "Zwei-Faktor zurücksetzen" reads as "Konto zurücksetzen", and an operator who
                 believes that escalates instead of helping. This is the server's actual behaviour. -->
            <p class="rounded-2xl bg-gray-50 px-4 py-3 text-gray-600">
              Verknüpfte Konten, die Passwort-Anmeldung und vergebene Rollen bleiben unverändert.
            </p>
            <p class="rounded-2xl bg-red-50 px-4 py-3 font-semibold text-red-700" role="alert">
              Dieser Schritt lässt sich nicht rückgängig machen.
            </p>
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <button type="button" #cancelButton (click)="cancel()"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Abbrechen
            </button>
            <button type="button" (click)="confirm()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-red-600 px-5 py-2.5 text-sm
                           font-semibold text-white shadow-lg shadow-red-100 transition-all duration-200
                           hover:bg-red-700 hover:shadow-xl active:scale-95 disabled:cursor-not-allowed
                           disabled:opacity-30 disabled:grayscale">
              Ja, zurücksetzen
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class TwoFactorResetDialogComponent {
  /** The account whose factor is about to go. Set once; the parent mounts a fresh instance per confirmation. */
  readonly account = input.required<AccountIdentity>();

  /** Disables the confirm button while the reset is in flight, so one click cannot become two. */
  readonly busy = input(false);

  /** Confirmed — the parent makes the call and closes the dialog. */
  readonly confirmed = output<void>();

  /** Cancel button, backdrop click, or Escape — all the same outcome: nothing changes. */
  readonly cancelled = output<void>();

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  protected readonly name = computed(() => accountName(this.account()));

  protected readonly provider = computed(() => providerLabel(this.account().provider));

  /**
   * The address, or `null` when it is already the heading.
   *
   * {@link accountName} falls back to the address when there is no display name, so printing both unfiltered
   * would show the same string on two consecutive lines — which reads as a broken render, not as data.
   */
  protected readonly address = computed<string | null>(() => {
    const mail = this.account().email?.trim();
    return mail && mail !== this.name() ? mail : null;
  });

  constructor() {
    // Focus starts on Cancel: the safe answer to a destructive prompt should be the one a stray Enter hits.
    effect(() => {
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  protected confirm(): void {
    if (this.busy()) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
